import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { 
  MessageCircle, X, Send, Bot, Loader2, 
  TrendingUp, DollarSign, Calendar, Eye, 
  Star, BadgeCheck, ChevronRight, Phone,
  HelpCircle, Lightbulb, BarChart3
} from 'lucide-react';
import authAxios, { API } from '../utils/authAxios';

function ProfessionalAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('menu'); // menu, chat, stats, tips, contact
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [sessionId] = useState(() => 'prof_' + Math.random().toString(36).substr(2, 9));
  const messagesEndRef = useRef(null);

  useEffect(function() {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  async function loadStats() {
    setLoadingStats(true);
    try {
      const res = await authAxios.get(API + '/professional/quick-stats', { withCredentials: true });
      setStats(res.data);
    } catch (e) {
      console.error('Error loading stats:', e);
    }
    setLoadingStats(false);
  }

  async function sendMessage() {
    if (!inputValue.trim() || isLoading) return;
    
    const userMsg = inputValue.trim();
    setInputValue('');
    setMessages(function(prev) { return [...prev, { role: 'user', content: userMsg }]; });
    setIsLoading(true);
    
    try {
      const res = await authAxios.post(API + '/professional/chat', {
        message: userMsg,
        session_id: sessionId
      }, { withCredentials: true });
      
      setMessages(function(prev) { return [...prev, { role: 'assistant', content: res.data.response }]; });
    } catch (e) {
      setMessages(function(prev) { return [...prev, { role: 'assistant', content: 'Lo siento, hubo un error. Por favor intenta de nuevo.' }]; });
    }
    setIsLoading(false);
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function startChat(initialMessage) {
    setMessages([{ role: 'assistant', content: initialMessage }]);
    setActiveTab('chat');
  }

  const tips = [
    { title: 'Completa tu perfil al 100%', desc: 'Los perfiles completos reciben 3x más contactos' },
    { title: 'Agrega una foto profesional', desc: 'Genera más confianza en los pacientes' },
    { title: 'Responde rápido a las consultas', desc: 'Mejora tu posición en el directorio' },
    { title: 'Pide reseñas a tus pacientes', desc: 'Las reseñas aumentan las conversiones' },
    { title: 'Ofrece primera sesión con descuento', desc: 'Atrae nuevos pacientes más fácilmente' },
    { title: 'Activa el plan Premium', desc: 'Aparece primero en las búsquedas' }
  ];

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={function() { setIsOpen(!isOpen); if (!stats) loadStats(); }}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full shadow-lg flex items-center justify-center text-white hover:scale-105 transition-transform z-50"
        data-testid="professional-assistant-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <HelpCircle className="w-6 h-6" />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-24 right-6 w-[380px] h-[500px] bg-white border shadow-2xl rounded-2xl overflow-hidden z-50 flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Asistente Profesional</h3>
                <p className="text-xs text-blue-100">Tu apoyo en SinAdicciones</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <CardContent className="flex-1 overflow-y-auto p-0">
            {/* Menu Tab */}
            {activeTab === 'menu' && (
              <div className="p-4 space-y-3">
                <p className="text-sm text-gray-600 mb-4">¿En qué puedo ayudarte hoy?</p>
                
                <button 
                  onClick={function() { setActiveTab('stats'); loadStats(); }}
                  className="w-full p-3 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 text-left transition-colors border border-emerald-200"
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="font-medium text-gray-800">Mis Estadísticas</p>
                      <p className="text-xs text-gray-500">Citas, ingresos y métricas</p>
                    </div>
                  </div>
                </button>

                <button 
                  onClick={function() { setActiveTab('tips'); }}
                  className="w-full p-3 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 text-left transition-colors border border-amber-200"
                >
                  <div className="flex items-center gap-3">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-gray-800">Consejos y Tips</p>
                      <p className="text-xs text-gray-500">Mejora tu perfil y atrae más pacientes</p>
                    </div>
                  </div>
                </button>

                <button 
                  onClick={function() { startChat('¡Hola! Soy tu asistente en SinAdicciones. Puedo ayudarte con:\n\n• **Precios y comisiones** de la plataforma\n• **Cómo mejorar tu perfil** para atraer más pacientes\n• **Dudas técnicas** sobre citas y pagos\n• **Plan Premium** y sus beneficios\n\n¿Qué te gustaría saber?'); }}
                  className="w-full p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-left transition-colors border border-blue-200"
                >
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-gray-800">Chat con Asistente</p>
                      <p className="text-xs text-gray-500">Preguntas sobre la plataforma</p>
                    </div>
                  </div>
                </button>

                <button 
                  onClick={function() { setActiveTab('contact'); }}
                  className="w-full p-3 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 text-left transition-colors border border-purple-200"
                >
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-gray-800">Contacto Soporte</p>
                      <p className="text-xs text-gray-500">Habla con nuestro equipo</p>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && (
              <div className="p-4">
                <button onClick={function() { setActiveTab('menu'); }} className="text-sm text-blue-600 mb-4 flex items-center gap-1 hover:underline">
                  ← Volver al menú
                </button>
                
                {loadingStats ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : stats ? (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-800 mb-3">📊 Resumen Rápido</h4>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
                        <Calendar className="w-5 h-5 text-blue-600 mb-1" />
                        <p className="text-2xl font-bold text-blue-700">{stats.total_appointments}</p>
                        <p className="text-xs text-blue-600">Total Citas</p>
                      </div>
                      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                        <TrendingUp className="w-5 h-5 text-emerald-600 mb-1" />
                        <p className="text-2xl font-bold text-emerald-700">{stats.completed_appointments}</p>
                        <p className="text-xs text-emerald-600">Completadas</p>
                      </div>
                      <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
                        <Eye className="w-5 h-5 text-amber-600 mb-1" />
                        <p className="text-2xl font-bold text-amber-700">{stats.profile_views}</p>
                        <p className="text-xs text-amber-600">Visitas Perfil</p>
                      </div>
                      <div className="p-3 rounded-xl bg-purple-50 border border-purple-100">
                        <DollarSign className="w-5 h-5 text-purple-600 mb-1" />
                        <p className="text-2xl font-bold text-purple-700">${(stats.net_earnings || 0).toLocaleString('es-CL')}</p>
                        <p className="text-xs text-purple-600">Ingresos Netos</p>
                      </div>
                    </div>

                    <div className="mt-4 p-3 rounded-xl bg-gray-50 border">
                      <p className="text-sm text-gray-600">
                        <strong>Comisión plataforma:</strong> ${(stats.platform_commission || 0).toLocaleString('es-CL')} (35%)
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        <strong>Citas pendientes:</strong> {stats.pending_appointments}
                      </p>
                    </div>

                    <div className="flex gap-2 mt-3">
                      {stats.is_verified && (
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                          <BadgeCheck className="w-3 h-3" /> Verificado
                        </span>
                      )}
                      {stats.is_premium && (
                        <span className="flex items-center gap-1 text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                          <Star className="w-3 h-3" /> Premium
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No se pudieron cargar las estadísticas</p>
                )}
              </div>
            )}

            {/* Tips Tab */}
            {activeTab === 'tips' && (
              <div className="p-4">
                <button onClick={function() { setActiveTab('menu'); }} className="text-sm text-blue-600 mb-4 flex items-center gap-1 hover:underline">
                  ← Volver al menú
                </button>
                <h4 className="font-semibold text-gray-800 mb-3">💡 Consejos para Crecer</h4>
                <div className="space-y-2">
                  {tips.map(function(tip, i) {
                    return (
                      <div key={i} className="p-3 rounded-lg bg-gray-50 border hover:bg-gray-100 transition-colors">
                        <p className="font-medium text-gray-800 text-sm">{tip.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{tip.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === 'contact' && (
              <div className="p-4">
                <button onClick={function() { setActiveTab('menu'); }} className="text-sm text-blue-600 mb-4 flex items-center gap-1 hover:underline">
                  ← Volver al menú
                </button>
                <h4 className="font-semibold text-gray-800 mb-4">📞 Contacto Soporte</h4>
                
                <div className="space-y-3">
                  <a 
                    href="https://wa.me/56939014212" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block p-4 rounded-xl bg-green-50 border border-green-200 hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">WhatsApp</p>
                        <p className="text-sm text-green-600">+56 9 3901 4212</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                    </div>
                  </a>

                  <a 
                    href="mailto:contacto@sinadicciones.org"
                    className="block p-4 rounded-xl bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                        <Send className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Email</p>
                        <p className="text-sm text-blue-600">contacto@sinadicciones.org</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
                    </div>
                  </a>
                </div>

                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>Horario de atención:</strong><br />
                    Lunes a Viernes: 9:00 - 18:00<br />
                    Sábado: 10:00 - 14:00
                  </p>
                </div>
              </div>
            )}

            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div className="flex flex-col h-full">
                <div className="p-2">
                  <button onClick={function() { setActiveTab('menu'); }} className="text-sm text-blue-600 flex items-center gap-1 hover:underline">
                    ← Volver al menú
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
                  {messages.map(function(msg, i) {
                    return (
                      <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                        <div className={'max-w-[85%] p-3 rounded-2xl text-sm ' + (msg.role === 'user' ? 'bg-blue-500 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md')}>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                        </div>
                      </div>
                    );
                  })}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-md">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}
          </CardContent>

          {/* Input (only for chat tab) */}
          {activeTab === 'chat' && (
            <div className="p-3 border-t bg-white">
              <div className="flex gap-2">
                <Input
                  value={inputValue}
                  onChange={function(e) { setInputValue(e.target.value); }}
                  onKeyPress={handleKeyPress}
                  placeholder="Escribe tu pregunta..."
                  className="flex-1 border-gray-200"
                  disabled={isLoading}
                />
                <Button 
                  onClick={sendMessage} 
                  disabled={isLoading || !inputValue.trim()}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </>
  );
}

export default ProfessionalAssistant;
